// src\components\portfolio\PortfolioDetailSkeleton.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

// Animation variants for staggered items
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

function PortfolioDetailSkeleton() {
  // Inject CSS animations into document head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }

      @keyframes pulse-subtle {
        0%, 100% {
          opacity: 0.4;
        }
        50% {
          opacity: 0.8;
        }
      }

      @keyframes bounce-skeleton {
        0%, 60%, 100% {
          transform: translateY(0);
        }
        30% {
          transform: translateY(-8px);
        }
      }

      @keyframes gradient-shift {
        0% {
          background-position: 0% 50%;
        }
        50% {
          background-position: 100% 50%;
        }
        100% {
          background-position: 0% 50%;
        }
      }

      .animate-shimmer {
        animation: shimmer 1.8s infinite;
      }

      .animate-pulse-subtle {
        animation: pulse-subtle 1.5s ease-in-out infinite;
      }

      .animate-bounce-skeleton {
        animation: bounce-skeleton 1.4s infinite ease-in-out;
      }

      .animate-gradient-shift {
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-base {
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
        border-radius: 8px;
      }

      .skeleton-card {
        background: var(--bg);
        border: 1px solid var(--border);
        border-radius: 12px;
        overflow: hidden;
        position: relative;
      }

      .skeleton-card .shimmer-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.05) 30%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.05) 70%,
          transparent 100%
        );
        animation: shimmer 2s infinite;
        pointer-events: none;
      }

      .dark .skeleton-card .shimmer-overlay {
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.03) 30%,
          rgba(255, 255, 255, 0.05) 50%,
          rgba(255, 255, 255, 0.03) 70%,
          transparent 100%
        );
      }

      .skeleton-text {
        height: 12px;
        border-radius: 4px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-text-sm {
        height: 10px;
        border-radius: 3px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-text-lg {
        height: 20px;
        border-radius: 6px;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-circle {
        border-radius: 50%;
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }

      .skeleton-image {
        background: linear-gradient(
          135deg,
          var(--border-light) 0%,
          var(--border) 50%,
          var(--border-light) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
        position: relative;
        overflow: hidden;
      }

      .skeleton-image .shimmer-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.06) 30%,
          rgba(255, 255, 255, 0.1) 50%,
          rgba(255, 255, 255, 0.06) 70%,
          transparent 100%
        );
        animation: shimmer 2s infinite;
        pointer-events: none;
      }

      .dark .skeleton-image .shimmer-overlay {
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.03) 30%,
          rgba(255, 255, 255, 0.05) 50%,
          rgba(255, 255, 255, 0.03) 70%,
          transparent 100%
        );
      }

      .skeleton-badge {
        border-radius: 9999px;
        background: linear-gradient(
          135deg,
          rgba(0,0,0,0.4) 0%,
          rgba(0,0,0,0.6) 50%,
          rgba(0,0,0,0.4) 100%
        );
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Generate random widths for text skeletons
  const randomWidth = (min = 40, max = 90) =>
    `${Math.floor(Math.random() * (max - min + 1)) + min}%`;

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen"
      style={{ backgroundColor: 'var(--bg)' }}
    >
      {/* Sticky Navigation Skeleton */}
      <div
        className="sticky top-[85px] z-30 backdrop-blur-md border-b"
        style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-2"
          >
            <div className="skeleton-text-lg w-24" />
          </motion.div>
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-3"
          >
            <div className="skeleton-text w-20 h-8 rounded-full" />
            <div className="skeleton-text w-16 h-8 rounded-full" />
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Main Content (8 columns) */}
          <div className="lg:col-span-8 space-y-6">
            {/* Image Slider Skeleton */}
            <motion.div
              variants={itemVariants}
              className="skeleton-card rounded-2xl overflow-hidden"
            >
              <div className="relative w-full aspect-video max-h-[480px] skeleton-image">
                <div className="shimmer-overlay" />
                {/* Image counter badge */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full skeleton-badge">
                  <div
                    className="w-12 h-4 skeleton-text-sm"
                    style={{ background: 'rgba(255,255,255,0.3)' }}
                  />
                </div>
                {/* Navigation buttons */}
                <div className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full skeleton-badge" />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full skeleton-badge" />
                {/* Badges */}
                <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
                  <div className="skeleton-badge px-3 py-1">
                    <div
                      className="w-16 h-4 skeleton-text-sm"
                      style={{ background: 'rgba(255,255,255,0.3)' }}
                    />
                  </div>
                  <div className="skeleton-badge px-3 py-1">
                    <div
                      className="w-16 h-4 skeleton-text-sm"
                      style={{ background: 'rgba(255,255,255,0.3)' }}
                    />
                  </div>
                  <div className="skeleton-badge px-3 py-1">
                    <div
                      className="w-16 h-4 skeleton-text-sm"
                      style={{ background: 'rgba(255,255,255,0.3)' }}
                    />
                  </div>
                </div>
                {/* Zoom badge */}
                <div className="absolute bottom-2 right-2 px-2 py-1 rounded-full skeleton-badge">
                  <div
                    className="w-12 h-4 skeleton-text-sm"
                    style={{ background: 'rgba(255,255,255,0.3)' }}
                  />
                </div>
              </div>
            </motion.div>

            {/* Title Skeleton */}
            <motion.div variants={itemVariants} className="space-y-3">
              <div className="skeleton-text-lg w-3/4" />
              <div className="flex flex-wrap gap-4">
                <div className="skeleton-text-sm w-28" />
                <div className="skeleton-text-sm w-28" />
                <div className="skeleton-text-sm w-28" />
                <div className="skeleton-text-sm w-28" />
              </div>
            </motion.div>

            {/* Stats Cards Skeleton */}
            <motion.div
              variants={containerVariants}
              className="grid grid-cols-2 sm:grid-cols-4 gap-3"
            >
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={i}
                  variants={itemVariants}
                  className="p-3 rounded-xl border text-center"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <div className="skeleton-circle w-6 h-6 mx-auto mb-1" />
                  <div className="skeleton-text w-12 h-6 mx-auto" />
                  <div className="skeleton-text-sm w-16 h-3 mx-auto" />
                </motion.div>
              ))}
            </motion.div>

            {/* Description Skeleton */}
            <motion.div variants={itemVariants} className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="skeleton-text w-1 h-6" />
                <div className="skeleton-text-lg w-40" />
              </div>
              <div className="space-y-2">
                <div className="skeleton-text w-full" />
                <div className="skeleton-text w-11/12" />
                <div className="skeleton-text w-full" />
                <div className="skeleton-text w-10/12" />
                <div className="skeleton-text w-full" />
                <div className="skeleton-text w-9/12" />
              </div>
            </motion.div>

            {/* Tags Skeleton */}
            <motion.div variants={itemVariants} className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="skeleton-text w-1 h-6" />
                <div className="skeleton-text-lg w-48" />
              </div>
              <div className="flex flex-wrap gap-2">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="skeleton-text w-20 h-8 rounded-full"
                  />
                ))}
              </div>
            </motion.div>

            {/* Inline Ad Skeleton */}
            <motion.div
              variants={itemVariants}
              className="p-3 rounded-xl border border-dashed text-center"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="skeleton-text-sm w-32 h-4 mx-auto" />
              <div className="skeleton-image min-h-[60px] w-full mt-2 rounded-lg" />
            </motion.div>
          </div>

          {/* Right Column - Sidebar (4 columns) */}
          <div className="lg:col-span-4 space-y-6">
            {/* Fiverr Card Skeleton */}
            <motion.div
              variants={itemVariants}
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-start gap-4">
                <div className="skeleton-circle w-14 h-14 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="skeleton-text-lg w-32" />
                    <div className="skeleton-text w-16 h-4 rounded-full" />
                  </div>
                  <div className="skeleton-text-sm w-48" />
                  <div className="flex items-center gap-2">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="skeleton-circle w-3 h-3" />
                    ))}
                    <div className="skeleton-text-sm w-12" />
                    <div className="skeleton-text-sm w-20" />
                  </div>
                  <div className="skeleton-text w-32 h-8 rounded-lg" />
                </div>
              </div>
            </motion.div>

            {/* Project Details Skeleton */}
            <motion.div
              variants={itemVariants}
              className="p-4 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="skeleton-text w-1 h-6" />
                <div className="skeleton-text-lg w-32" />
              </div>
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="flex justify-between">
                    <div className="skeleton-text-sm w-20" />
                    <div className="skeleton-text-sm w-24" />
                  </div>
                ))}
              </div>
            </motion.div>

            {/* CTA Card Skeleton */}
            <motion.div
              variants={itemVariants}
              className="p-4 rounded-xl border text-center"
              style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--border-light)',
              }}
            >
              <div className="skeleton-text-lg w-40 mx-auto mb-2" />
              <div className="skeleton-text-sm w-48 mx-auto mb-3" />
              <div className="skeleton-text w-32 h-8 mx-auto rounded-full" />
            </motion.div>

            {/* Sidebar Ad Skeleton */}
            <motion.div
              variants={itemVariants}
              className="p-3 rounded-xl border border-dashed text-center"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="skeleton-text-sm w-32 h-4 mx-auto" />
              <div className="skeleton-image min-h-[250px] w-full mt-2 rounded-lg" />
            </motion.div>
          </div>
        </div>

        {/* Suggestions Section Skeleton */}
        <motion.div
          variants={containerVariants}
          className="mt-8 pt-8 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-3 mb-6"
          >
            <div className="skeleton-text w-1 h-8" />
            <div className="skeleton-text-lg w-48" />
            <div className="skeleton-text w-20 h-6 rounded-full" />
          </motion.div>

          {/* 3 Suggestion Rows */}
          {[...Array(3)].map((_, sectionIdx) => (
            <motion.div
              key={sectionIdx}
              variants={itemVariants}
              className="mb-8"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="skeleton-circle w-4 h-4" />
                  <div className="skeleton-text-lg w-32" />
                </div>
                <div className="skeleton-text-sm w-20" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[...Array(6)].map((_, idx) => (
                  <div
                    key={idx}
                    className="skeleton-card rounded-lg overflow-hidden border"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <div className="skeleton-image aspect-video">
                      <div className="shimmer-overlay" />
                    </div>
                    <div className="p-2 space-y-1">
                      <div className="skeleton-text-sm w-full" />
                      <div className="flex items-center gap-2">
                        <div className="skeleton-text-sm w-16" />
                        <div className="skeleton-text-sm w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}

          {/* Bottom Ad Skeleton */}
          <motion.div
            variants={itemVariants}
            className="p-4 rounded-xl border border-dashed text-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="skeleton-text-sm w-32 h-4 mx-auto" />
            <div className="skeleton-image min-h-[90px] w-full mt-2 rounded-lg" />
          </motion.div>
        </motion.div>

        {/* Loading Bounce Indicator */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center gap-3 mt-8"
        >
          <div
            className="w-2 h-2 rounded-full animate-bounce-skeleton"
            style={{
              backgroundColor: 'var(--text)',
              animationDelay: '0s',
            }}
          />
          <div
            className="w-2 h-2 rounded-full animate-bounce-skeleton"
            style={{
              backgroundColor: 'var(--text)',
              animationDelay: '0.2s',
            }}
          />
          <div
            className="w-2 h-2 rounded-full animate-bounce-skeleton"
            style={{
              backgroundColor: 'var(--text)',
              animationDelay: '0.4s',
            }}
          />
        </motion.div>
      </div>
    </motion.div>
  );
}

export default PortfolioDetailSkeleton;
